/**
 * vm-size-drawer.js: "Find my size" drawer on the PDP (owner: PDP package).
 * Markup: blocks/vm-size-link.liquid + snippets/vm-size-drawer.liquid.
 *
 * A native modal <dialog>: showModal() makes the page inert (focus is trapped),
 * Esc closes it, a backdrop click closes it, focus returns to the trigger.
 * Size buttons select that size in Horizon's variant picker (they click the
 * picker's own radio, or set its <select>), so Horizon does the variant change.
 * The size table itself is Liquid (shared snippet vm-size-table), in / cm is CSS.
 */
class VmSizeDrawer extends HTMLElement {
  #ac = null;
  #returnTo = null;
  #from = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.trigger = this.querySelector('[data-vm-size-open]');
    if (!this.dialog || !this.trigger || typeof this.dialog.showModal !== 'function') return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.trigger.setAttribute('role', 'button');
    this.trigger.addEventListener('click', (e) => (e.preventDefault(), this.open()), opts);
    this.trigger.addEventListener('keydown', (e) => e.key === ' ' && (e.preventDefault(), this.open()), opts);
    this.dialog.addEventListener('click', this.#onClick, opts);
    this.dialog.addEventListener('close', () => this.#restore(), opts);
    // v5.3 (L4): "Find my size" also lives in the Size head row and the Size and fit drawer
    // (patch 12, data-vm-size-open-ext); those buttons are re-rendered by Horizon, so delegate.
    const scope = this.closest('.shopify-section') || document;
    for (const b of scope.querySelectorAll('[data-vm-size-open-ext]')) b.setAttribute('aria-controls', this.dialog.id);
    scope.addEventListener('click', (e) => {
      const ext = /** @type {Element} */ (e.target).closest?.('[data-vm-size-open-ext]');
      if (!ext || this.dialog.contains(ext)) return;
      e.preventDefault();
      this.open(ext);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.dialog?.open) this.dialog.close();
  }

  open(from) {
    this.#returnTo = null;
    this.#from = from || null;
    this.#sync();
    this.dialog.showModal();
  }

  /** Focus goes back to the size just picked (re-found after Horizon's morph) or the trigger. */
  #restore() {
    let to = this.#returnTo;
    if (typeof to === 'string') to = this.#radio(to);
    const back = this.#from?.isConnected ? this.#from : this.trigger;
    (to?.isConnected ? to : back).focus({ preventScroll: true });
  }

  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target === this.dialog || target.closest('[data-vm-size-close]')) return this.dialog.close();
    const pick = target.closest('[data-vm-size-pick]');
    if (pick) this.#pick(pick.getAttribute('data-vm-size-pick'));
  };

  #picker() {
    const scope = this.closest('.shopify-section') || document;
    return scope.querySelector('variant-picker');
  }

  #radio(value) {
    const name = CSS.escape(this.dataset.option || 'Size');
    return this.#picker()?.querySelector(`input[type="radio"][data-option-name="${name}"][value="${CSS.escape(value)}"]`);
  }

  /** Mirror the picker's current size onto the drawer buttons. */
  #sync() {
    for (const btn of this.dialog.querySelectorAll('[data-vm-size-pick]')) {
      const radio = this.#radio(btn.getAttribute('data-vm-size-pick'));
      if (radio) btn.setAttribute('aria-pressed', String(radio.checked));
    }
  }

  #pick(value) {
    const radio = this.#radio(value);
    if (radio) {
      if (!radio.checked) radio.click();
      this.#returnTo = value;
    } else {
      const select = this.#picker()?.querySelector(`select[name="options[${CSS.escape(this.dataset.option || 'Size')}]"]`);
      if (select) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        this.#returnTo = select;
      }
    }
    this.dialog.close();
  }
}

if (!customElements.get('vm-size-drawer')) customElements.define('vm-size-drawer', VmSizeDrawer);
